import { uploadFileToS3 } from '../../api/photos';
import {
  getQuoteImportUploadUrl,
  type AnalyzeQuoteRequest,
  type AnalyzeQuoteResponse,
} from '../../api/quote-import';

/** What a vendor's quote or invoice may be: a PDF or a picture of one. */
export const DOCUMENT_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/tiff',
];
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/** Why a file cannot be read, in words for the person who chose it; null when it can. */
export function documentFileProblem(file: File): string | null {
  if (!DOCUMENT_FILE_TYPES.includes(file.type)) {
    return `"${file.name}" is not a supported file type. Please upload a PDF or image.`;
  }
  if (file.size > MAX_DOCUMENT_BYTES) return `"${file.name}" exceeds the 25 MB limit.`;
  return null;
}

/**
 * Upload a vendor's document and have the AI read it: the one path an invoice
 * import and a quote's "read from the file" share. The answer names the
 * document the upload became, to file where the figures go.
 */
export async function uploadAndRead(
  file: File,
  eventId: string,
  analyze: (request: AnalyzeQuoteRequest) => Promise<AnalyzeQuoteResponse>,
): Promise<AnalyzeQuoteResponse> {
  // The server names the file by its own folder, so a slash in a name the
  // phone gave it cannot put it anywhere else.
  const fileName = file.name.replace(/[/\\]/g, '_');
  const presigned = await getQuoteImportUploadUrl({ fileName, mimeType: file.type });
  await uploadFileToS3(presigned.uploadUrl, file);
  return analyze({ eventId, s3Key: presigned.s3Key, fileName, mimeType: file.type });
}
